import React, { useEffect, useState } from 'react';
import { noop } from 'lodash';
import { Container } from '@fiverr-private/layout_components';
import { raiseErrorToast, useIdentificationContext } from '@fiverr-private/identification_core';
import {
    LazyBusinessInfoFormComponent,
    updateBusinessProfile,
    updateMemberProfile,
} from '@fiverr-private/pro_migration';
import { Button } from '@fiverr-private/button';
import { useSignUpContext } from '../../SignUpContext';
import { globalBigQueryReporter, globalGrafanaReporter } from '../../../events';
import { translate } from '../../../utils/translate';
import { useStepsContext } from '../../StepsContext';

export const BusinessInfoStep: React.FC & { PreloadView: React.FC } = () => {
    const { skipActivation, finishSigningFlowAndNavigate, enableShouldNavigateOnClose } = useIdentificationContext();
    const { organizationId } = useSignUpContext();
    const { goToStep } = useStepsContext();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        globalGrafanaReporter.count('identification.%{platform}.business_info.show');
        globalBigQueryReporter.send('identification.business_info.show');
    }, []);

    const handleSubmit: React.ComponentProps<typeof LazyBusinessInfoFormComponent>['handleSubmit'] = async (
        businessInfo
    ) => {
        if (!organizationId) {
            return;
        }
        setLoading(true);

        globalBigQueryReporter.send('identification.business_info.submit', {
            companySize: businessInfo.numberOfEmployees,
            companyRole: businessInfo.field,
        });

        try {
            await Promise.all([
                updateBusinessProfile({
                    organizationId,
                    companyName: '',
                    numberOfEmployees: String(businessInfo.numberOfEmployees),
                    source: 'BusinessInfoStep',
                }),
                businessInfo.field && updateMemberProfile({ field: businessInfo.field, source: 'BusinessInfoStep' }),
            ]);
        } catch (err) {
            globalBigQueryReporter.send('identification.business_info.failed');
            globalGrafanaReporter.count('identification.%{platform}.business_info.failed');

            raiseErrorToast();
            setLoading(false);
        }

        if (skipActivation) {
            await finishSigningFlowAndNavigate();
            return;
        }

        enableShouldNavigateOnClose();

        goToStep('code_activation', {
            resetHistory: true,
        });
    };

    return (
        <Container display="flex" direction="column" flexGrow="1" gap="10">
            <LazyBusinessInfoFormComponent handleSubmit={handleSubmit} />
            <Container marginTop="auto">
                <Button loading={loading} form={LazyBusinessInfoFormComponent.FORM_ID} type="submit" fullWidth>
                    {translate('steps.business_info.continue')}
                </Button>
            </Container>
        </Container>
    );
};

const PreloadView = () => {
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        setIsLoaded(true);
    }, []);

    return isLoaded ? null : (
        <div style={{ display: 'none' }}>
            <LazyBusinessInfoFormComponent handleSubmit={noop} />
        </div>
    );
};

// README: We have to save layout bundle size, and don't have blink on render step (during loading)
BusinessInfoStep.PreloadView = PreloadView;
